import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  RadioGroup,
  RadioGroupItem,
} from "@/components/ui/radio-group";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { GripVertical, ArrowRight, ArrowLeft, Sparkles, Check } from "lucide-react";

const INDUSTRIES = ["Technology", "Finance", "Healthcare", "Legal", "Education", "Creative", "Logistics & Operations", "Other"];

export default function Onboarding() {
  const { user, refresh } = useAuth();
  const [step, setStep] = useState(0);
  const [industry, setIndustry] = useState(user?.industry || "Technology");
  const [analytics, setAnalytics] = useState(user?.analytics_consent || false);
  const [marketing, setMarketing] = useState(user?.marketing_consent || false);
  const [hierarchy, setHierarchy] = useState(user?.hierarchy || []);
  const [position, setPosition] = useState(user?.settings?.widget_position || "bottom-right");
  const [hotkey, setHotkey] = useState(user?.settings?.hotkey || "Ctrl+Shift+Space");
  const [threshold, setThreshold] = useState(user?.settings?.distraction_threshold_seconds || 45);
  const [autoDismiss, setAutoDismiss] = useState(user?.settings?.auto_dismiss_seconds || 8);
  const nav = useNavigate();

  const next = () => setStep((s) => Math.min(s + 1, 6));
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const submitConsent = async () => {
    await api.put("/auth/consent", { analytics_consent: analytics, marketing_consent: marketing });
    next();
  };

  const submitIndustry = async () => {
    // pre-load industry-default hierarchy from backend
    const { data } = await api.put("/hierarchy", {
      items: defaultForIndustry(industry).map((it, i) => ({ ...it, rank: i + 1 })),
    });
    setHierarchy(data.items);
    next();
  };

  const moveItem = (from, to) => {
    if (to < 0 || to >= hierarchy.length) return;
    const next = [...hierarchy];
    const [item] = next.splice(from, 1);
    next.splice(to, 0, item);
    next.forEach((it, i) => (it.rank = i + 1));
    setHierarchy(next);
  };

  const saveHierarchy = async () => {
    await api.put("/hierarchy", { items: hierarchy.map((h) => ({ name: h.name, kind: h.kind, rank: h.rank, category: h.category })) });
    next();
  };

  const saveSettings = async () => {
    await api.put("/settings", {
      widget_position: position,
      hotkey,
      distraction_threshold_seconds: threshold,
      auto_dismiss_seconds: autoDismiss,
    });
    next();
  };

  const finish = async () => {
    await api.post("/auth/complete-onboarding");
    await refresh();
    toast.success("You're all set.");
    nav("/app");
  };

  const screens = [
    {
      title: "Welcome to WYLO",
      key: "welcome",
      body: (
        <div className="text-center py-10">
          <p className="text-xs uppercase tracking-[0.2em] text-[#2C6BAC] font-semibold mb-4">WYLO</p>
          <h1 className="font-display text-5xl tracking-tighter text-[#1A1A2E]">
            Hi {user?.name?.split(" ")[0]}.<br />Let's get you set up.
          </h1>
          <p className="mt-6 text-[#555568] max-w-md mx-auto">
            About 90 seconds, six quick screens. You'll be back in flow before you know it.
          </p>
          <Button onClick={next} className="mt-10 bg-[#2C6BAC] hover:bg-[#1A3A5C] text-white h-12 px-7" data-testid="onb-start">
            Get started <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      ),
    },
    {
      title: "Your account",
      key: "account",
      body: (
        <div>
          <h2 className="font-display text-3xl tracking-tight text-[#1A1A2E]">You're already in.</h2>
          <p className="text-[#555568] mt-2">We've created your account - no extra steps here.</p>
          <div className="mt-6 rounded-xl border border-[#E8ECF2] bg-[#F8F9FC] p-5 text-sm space-y-2">
            <p><span className="text-[#555568]">Name:</span> <span className="font-semibold">{user?.name}</span></p>
            <p><span className="text-[#555568]">Email:</span> <span className="font-mono">{user?.email}</span></p>
            <p><span className="text-[#555568]">Tier:</span> <span className="uppercase tracking-wider text-[#2C6BAC] font-semibold">{user?.tier}</span></p>
          </div>
          <div className="mt-8 flex gap-2">
            <Button variant="outline" onClick={back}><ArrowLeft className="h-4 w-4 mr-2" /> Back</Button>
            <Button onClick={next} className="bg-[#2C6BAC] hover:bg-[#1A3A5C] text-white" data-testid="onb-account-next">
              Continue <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </div>
      ),
    },
    {
      title: "What kind of work?",
      key: "industry",
      body: (
        <div>
          <h2 className="font-display text-3xl tracking-tight text-[#1A1A2E]">What industry do you work in?</h2>
          <p className="text-[#555568] mt-2">We'll pre-populate your priority hierarchy based on this.</p>
          <div className="mt-6">
            <Select value={industry} onValueChange={setIndustry}>
              <SelectTrigger data-testid="onb-industry-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {INDUSTRIES.map((i) => (
                  <SelectItem key={i} value={i}>{i}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="mt-8 flex gap-2">
            <Button variant="outline" onClick={back}><ArrowLeft className="h-4 w-4 mr-2" /> Back</Button>
            <Button onClick={submitIndustry} className="bg-[#2C6BAC] hover:bg-[#1A3A5C] text-white" data-testid="onb-industry-next">
              Continue <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </div>
      ),
    },
    {
      title: "Your consent",
      key: "consent",
      body: (
        <div>
          <h2 className="font-display text-3xl tracking-tight text-[#1A1A2E]">Privacy is a product value.</h2>
          <p className="text-[#555568] mt-2">Choose what you're comfortable with. Necessary data is required for WYLO to function.</p>
          <div className="mt-6 space-y-4">
            <Row label="Necessary" desc="Auth, session, focus signals. Required." checked locked />
            <Row label="Analytics & usage data" desc="Anonymised. Helps us improve WYLO." checked={analytics} onChange={setAnalytics} testid="onb-consent-analytics" />
            <Row label="Marketing communications" desc="Occasional product updates. Unsubscribe any time." checked={marketing} onChange={setMarketing} testid="onb-consent-marketing" />
          </div>
          <div className="mt-8 flex gap-2">
            <Button variant="outline" onClick={back}><ArrowLeft className="h-4 w-4 mr-2" /> Back</Button>
            <Button onClick={submitConsent} className="bg-[#2C6BAC] hover:bg-[#1A3A5C] text-white" data-testid="onb-consent-next">
              Continue <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </div>
      ),
    },
    {
      title: "Priority hierarchy",
      key: "hierarchy",
      body: (
        <div>
          <h2 className="font-display text-3xl tracking-tight text-[#1A1A2E]">Rank your work.</h2>
          <p className="text-[#555568] mt-2">Drag to reorder. Rank 1 is your highest-priority work.</p>
          <div className="mt-6 max-h-[420px] overflow-y-auto pr-1 space-y-2" data-testid="onb-hierarchy-list">
            {hierarchy.map((it, i) => (
              <div key={i} className="flex items-center gap-3 rounded-md border border-[#E8ECF2] bg-white p-3">
                <GripVertical size={16} className="text-[#555568]" />
                <span className="font-mono text-xs text-[#2C6BAC] w-6">#{it.rank}</span>
                <div className="flex-1">
                  <p className="text-sm font-semibold text-[#1A1A2E]">{it.name}</p>
                  <p className="text-xs text-[#555568]">{it.category} · {it.kind}</p>
                </div>
                <div className="flex gap-1">
                  <Button size="sm" variant="ghost" onClick={() => moveItem(i, i - 1)} disabled={i === 0}>↑</Button>
                  <Button size="sm" variant="ghost" onClick={() => moveItem(i, i + 1)} disabled={i === hierarchy.length - 1}>↓</Button>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-6 flex gap-2">
            <Button variant="outline" onClick={back}><ArrowLeft className="h-4 w-4 mr-2" /> Back</Button>
            <Button onClick={saveHierarchy} className="bg-[#2C6BAC] hover:bg-[#1A3A5C] text-white" data-testid="onb-hierarchy-next">
              Continue <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </div>
      ),
    },
    {
      title: "Widget setup",
      key: "widget",
      body: (
        <div>
          <h2 className="font-display text-3xl tracking-tight text-[#1A1A2E]">Configure your refocus widget.</h2>
          <p className="text-[#555568] mt-2">Choose where it appears and how you'll trigger it manually.</p>

          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <Label className="text-xs uppercase tracking-[0.18em] text-[#555568]">Corner position</Label>
              <RadioGroup value={position} onValueChange={setPosition} className="mt-3 grid grid-cols-2 gap-3">
                {["top-left", "top-right", "bottom-left", "bottom-right"].map((p) => (
                  <label
                    key={p}
                    className={`relative cursor-pointer rounded-md border px-3 py-3 text-sm flex items-center gap-2 ${
                      position === p ? "border-[#1A3A5C] bg-[#E8ECF2]" : "border-[#E8ECF2] bg-white"
                    }`}
                  >
                    <RadioGroupItem value={p} />
                    {p}
                  </label>
                ))}
              </RadioGroup>

              <Label className="text-xs uppercase tracking-[0.18em] text-[#555568] mt-6 block">FACE hotkey</Label>
              <select
                value={hotkey}
                onChange={(e) => setHotkey(e.target.value)}
                className="mt-2 w-full rounded-md border border-[#E8ECF2] bg-white px-3 py-2 text-sm"
                data-testid="onb-hotkey"
              >
                <option>Ctrl+Shift+Space</option>
                <option>Cmd+Shift+Space</option>
                <option>Alt+Shift+F</option>
              </select>

              <Label className="text-xs uppercase tracking-[0.18em] text-[#555568] mt-6 block">
                Distraction threshold · {threshold}s
              </Label>
              <Slider value={[threshold]} min={15} max={120} step={5} onValueChange={([v]) => setThreshold(v)} className="mt-3" data-testid="onb-threshold" />

              <Label className="text-xs uppercase tracking-[0.18em] text-[#555568] mt-6 block">
                Auto-dismiss · {autoDismiss}s
              </Label>
              <Slider value={[autoDismiss]} min={3} max={20} step={1} onValueChange={([v]) => setAutoDismiss(v)} className="mt-3" data-testid="onb-autodismiss" />
            </div>

            {/* Preview */}
            <div className="relative rounded-xl border border-[#E8ECF2] bg-[#F8F9FC] aspect-[4/3] overflow-hidden">
              <div className={`absolute ${position.includes("top") ? "top-3" : "bottom-3"} ${position.includes("left") ? "left-3" : "right-3"} bg-white/80 backdrop-blur-xl border border-white/60 rounded-xl p-3 w-44 shadow-md soft-pulse`}>
                <div className="flex items-center gap-2">
                  <div className="h-8 w-8 rounded-md bg-[#1A3A5C] flex items-center justify-center">
                    <span className="text-white text-xs font-bold">Fg</span>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.18em] text-[#2C6BAC] font-semibold">Refocus</p>
                    <p className="text-xs font-semibold text-[#1A1A2E] -mt-0.5">Last task</p>
                  </div>
                </div>
                <p className="text-[10px] text-[#555568] font-mono mt-1">⏱ 00:42 ago</p>
              </div>
              <p className="absolute top-3 right-3 left-3 text-center text-xs text-[#555568] uppercase tracking-[0.18em]">
                Live preview
              </p>
            </div>
          </div>

          <div className="mt-8 flex gap-2">
            <Button variant="outline" onClick={back}><ArrowLeft className="h-4 w-4 mr-2" /> Back</Button>
            <Button onClick={saveSettings} className="bg-[#2C6BAC] hover:bg-[#1A3A5C] text-white" data-testid="onb-widget-next">
              Continue <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </div>
      ),
    },
    {
      title: "You're in.",
      key: "done",
      body: (
        <div className="text-center py-10">
          <div className="inline-flex h-16 w-16 rounded-full bg-[#1D9E75]/15 items-center justify-center mb-6">
            <Check className="text-[#1D9E75]" size={28} />
          </div>
          <h2 className="font-display text-4xl tracking-tight text-[#1A1A2E]">You're set up.</h2>
          <p className="mt-3 text-[#555568] max-w-md mx-auto">
            Your dashboard is ready. The OS agent and browser extension are managed in app settings - for now, take a tour.
          </p>
          <Button onClick={finish} className="mt-8 bg-[#2C6BAC] hover:bg-[#1A3A5C] text-white h-12 px-7" data-testid="onb-finish">
            Go to my dashboard <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="min-h-screen bg-[#F8F9FC]">
      <div className="max-w-3xl mx-auto px-6 pt-16 pb-24">
        <div className="flex items-center justify-between mb-10">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-md bg-[#1A3A5C] flex items-center justify-center">
              <span className="font-display font-bold text-[#F8F9FC] text-sm">W</span>
            </div>
            <span className="font-display text-xl font-bold tracking-tighter text-[#1A1A2E]">wylo</span>
          </div>
          <span className="text-xs uppercase tracking-[0.18em] text-[#555568] font-mono" data-testid="onb-step-indicator">
            Step {step + 1} / {screens.length}
          </span>
        </div>
        <div className="h-1 rounded-full bg-[#E8ECF2] mb-10 overflow-hidden">
          <div className="h-full bg-[#2C6BAC] transition-all" style={{ width: `${((step + 1) / screens.length) * 100}%` }} />
        </div>

        <motion.div
          key={step}
          initial={{ opacity: 0, x: 12 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.3 }}
          className="rounded-xl border border-[#E8ECF2] bg-white p-8"
          data-testid={`onb-screen-${screens[step].key}`}
        >
          {screens[step].body}
        </motion.div>
      </div>
    </div>
  );
}

function Row({ label, desc, checked, onChange, locked, testid }) {
  return (
    <div className="flex items-start gap-3 rounded-md border border-[#E8ECF2] bg-[#F8F9FC] p-3">
      <Checkbox checked={checked} onCheckedChange={onChange} disabled={locked} data-testid={testid} />
      <div>
        <p className="text-sm font-semibold text-[#1A1A2E]">{label}</p>
        <p className="text-xs text-[#555568]">{desc}</p>
      </div>
    </div>
  );
}

function defaultForIndustry(industry) {
  const map = {
    Technology: [
      ["VS Code", "app", "IDE"],
      ["GitHub", "website", "Development"],
      ["Stack Overflow", "website", "Reference"],
      ["Linear", "website", "Project Management"],
      ["Figma", "app", "Design"],
      ["Notion", "website", "Docs"],
      ["Slack", "app", "Communication"],
      ["Google Docs", "website", "Docs"],
      ["Jira", "website", "Project Management"],
    ],
    Finance: [
      ["Excel", "app", "Spreadsheets"],
      ["Bloomberg Terminal", "app", "Data"],
      ["SAP", "website", "ERP"],
      ["Outlook", "app", "Communication"],
      ["Salesforce", "website", "CRM"],
      ["PowerBI", "app", "Analytics"],
      ["Reuters", "website", "News"],
      ["Teams", "app", "Communication"],
      ["Google Docs", "website", "Docs"],
    ],
    Healthcare: [
      ["Epic", "app", "EHR"], ["Cerner", "app", "EHR"], ["Outlook", "app", "Communication"],
      ["Teams", "app", "Communication"], ["PubMed", "website", "Research"], ["Google Scholar", "website", "Research"],
      ["Excel", "app", "Spreadsheets"], ["Word", "app", "Docs"], ["OneDrive", "website", "Storage"],
    ],
    Legal: [
      ["Word", "app", "Docs"], ["Westlaw", "website", "Research"], ["LexisNexis", "website", "Research"],
      ["Outlook", "app", "Communication"], ["Teams", "app", "Communication"], ["Adobe Acrobat", "app", "Docs"],
      ["Excel", "app", "Spreadsheets"], ["OneDrive", "website", "Storage"], ["Clio", "website", "Practice Management"],
    ],
    Education: [
      ["Google Docs", "website", "Docs"], ["Google Classroom", "website", "Teaching"], ["Zoom", "app", "Communication"],
      ["Canvas", "website", "LMS"], ["Moodle", "website", "LMS"], ["Word", "app", "Docs"],
      ["PowerPoint", "app", "Presentations"], ["Notion", "website", "Notes"], ["Google Scholar", "website", "Research"],
    ],
    Creative: [
      ["Figma", "app", "Design"], ["Adobe Photoshop", "app", "Design"], ["Adobe Illustrator", "app", "Design"],
      ["Notion", "website", "Docs"], ["Slack", "app", "Communication"], ["Dribbble", "website", "Inspiration"],
      ["Behance", "website", "Inspiration"], ["Google Drive", "website", "Storage"], ["Miro", "website", "Whiteboard"],
    ],
    "Logistics & Operations": [
      ["Excel", "app", "Spreadsheets"], ["SAP", "website", "ERP"], ["Outlook", "app", "Communication"],
      ["Teams", "app", "Communication"], ["PowerBI", "app", "Analytics"], ["Slack", "app", "Communication"],
      ["Google Sheets", "website", "Spreadsheets"], ["Notion", "website", "Docs"], ["Asana", "website", "Project Management"],
    ],
    Other: [
      ["Google Docs", "website", "Docs"], ["Slack", "app", "Communication"], ["Notion", "website", "Docs"],
      ["Gmail", "website", "Communication"], ["Zoom", "app", "Communication"], ["Trello", "website", "Project Management"],
      ["Excel", "app", "Spreadsheets"], ["Word", "app", "Docs"], ["Outlook", "app", "Communication"],
    ],
  };
  return (map[industry] || map.Other).map(([n, k, c]) => ({ name: n, kind: k, category: c }));
}
